// Razorpay client utility
// Loads Razorpay checkout.js script & opens payment flow.
// Uses Cloud Functions for order creation & verification. Paid registrations
// must never be accepted directly from the browser.
import { RAZORPAY_KEY_ID, CF_BASE_URL } from "../firebase";

let razorpayScriptPromise = null;

const backendUnavailableMessage =
  "Coupon backend is not reachable. Deploy Firebase Functions and check REACT_APP_CF_BASE_URL.";

export const loadRazorpayScript = () =>
  razorpayScriptPromise || (razorpayScriptPromise = new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => {
      razorpayScriptPromise = null;
      resolve(false);
    };
    document.body.appendChild(script);
  }));

// Create Razorpay order via Cloud Function.
export const createOrder = async ({ receipt, email, collegeName, college, subjectId, subjectName }) => {
  if (!CF_BASE_URL) {
    throw new Error("Payment backend is not configured. Please contact support.");
  }

  const res = await fetch(`${CF_BASE_URL}/createRazorpayOrder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ receipt, email, collegeName, college, subjectId, subjectName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Unable to create payment order");
  }
  return await res.json();
};

export const verifyPayment = async ({
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
  registrationData,
}) => {
  if (!CF_BASE_URL) {
    throw new Error("Payment backend is not configured. Please contact support.");
  }

  const res = await fetch(`${CF_BASE_URL}/verifyRazorpayPayment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      registrationData,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Verification failed");
  }
  return await res.json();
};

export const validateCoupon = async ({ couponCode, email, selectedProgramId }) => {
  if (!CF_BASE_URL) {
    throw new Error("Coupon backend is not configured. Please contact support.");
  }

  let res;
  try {
    res = await fetch(`${CF_BASE_URL}/validateCoupon`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ couponCode, email, selectedProgramId }),
    });
  } catch (error) {
    throw new Error(backendUnavailableMessage);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Unable to validate coupon");
  }
  return await res.json();
};

export const redeemCouponRegistration = async ({ couponCode, registrationData }) => {
  if (!CF_BASE_URL) {
    throw new Error("Coupon backend is not configured. Please contact support.");
  }

  let res;
  try {
    res = await fetch(`${CF_BASE_URL}/redeemCouponAndRegister`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ couponCode, registrationData }),
    });
  } catch (error) {
    throw new Error(backendUnavailableMessage);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Coupon registration failed");
  }
  return await res.json();
};

export const openRazorpayCheckout = async ({
  amount,
  name,
  email,
  phone,
  description,
  orderId,
  onSuccess,
  onFailure,
  onDismiss,
}) => {
  const ok = await loadRazorpayScript();
  if (!ok) {
    onFailure?.("Razorpay SDK failed to load.");
    return;
  }

  const options = {
    key: RAZORPAY_KEY_ID,
    amount: amount * 100, // paise
    currency: "INR",
    name: "Kampus Campanion",
    description: description || "Internship Registration",
    image: `${window.location.origin}/assets/kampus-logo.svg`,
    ...(orderId ? { order_id: orderId } : {}),
    handler: (response) => {
      onSuccess?.(response);
    },
    prefill: { name, email, contact: phone },
    notes: { platform: "kampus-companion" },
    theme: { color: "#2563EB" },
    modal: {
      ondismiss: () => onDismiss?.(),
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", (resp) => onFailure?.(resp.error?.description || "Payment failed"));
  rzp.open();
};

